import React, { useState } from 'react';
import { Button, Modal, Form, Table, Alert } from 'react-bootstrap';

const toRow = (rule) => ({ field: rule.field, value: rule.value, email: [].concat(rule.email).join(', ') });
const emptyRow = () => ({ field: '', value: '', email: '' });

// Parse the editable rows into rules for the API; entirely blank rows are dropped
const parseRows = (rows) => {
  const rules = [];
  rows.forEach((row, index) => {
    const field = row.field.trim();
    const value = row.value.trim();
    const emails = row.email.split(/[\n,]+/).map(e => e.trim()).filter(e => e.length > 0);
    if (!field && !value && emails.length === 0) return;
    if (!field || !value) throw new Error(`Rule ${index + 1}: form field and value are required.`);
    if (emails.length === 0) throw new Error(`Rule ${index + 1}: at least one email is required.`);
    const invalid = emails.find(e => !e.includes('@'));
    if (invalid) throw new Error(`Rule ${index + 1}: invalid email format: ${invalid}`);
    rules.push({ field, value, email: emails });
  });
  return rules;
};

// Routing rules send a lead to other addresses when a submitted form field has a given value.
// Mounted while open, so the rows start from the saved rules each time.
function EmailRoutingEditor({ domain, routes, onHide, onSave }) {
  const [rows, setRows] = useState(() => routes.map(toRow));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Closing mid-save would let the late success handler close another domain's editor
  const close = () => {
    if (!saving) onHide();
  };

  const updateRow = (index, key, text) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, [key]: text } : row)));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    let rules;
    try {
      rules = parseRows(rows);
    } catch (err) {
      setError(err.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(rules);
    } catch (err) {
      setError(err.message || 'Failed to save routing rules.');
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={close} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Routing rules for {domain}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          <p className="text-muted">
            When a submitted form field has a rule's value, the lead goes to that rule's addresses instead of the
            domain's default addresses. The first matching rule wins, and case and macrons are ignored
            (Taupo matches Taupō). Leads that match no rule go to the default addresses.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          {rows.length === 0 ? (
            <Alert variant="info">No routing rules. Every lead goes to the default addresses.</Alert>
          ) : (
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Form field</th>
                  <th>Value</th>
                  <th>Send to</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control size="sm" placeholder="property_region" value={row.field}
                        onChange={(e) => updateRow(index, 'field', e.target.value)} />
                    </td>
                    <td>
                      <Form.Control size="sm" placeholder="Taupo" value={row.value}
                        onChange={(e) => updateRow(index, 'value', e.target.value)} />
                    </td>
                    <td>
                      <Form.Control size="sm" placeholder="office@example.com, other@example.com" value={row.email}
                        onChange={(e) => updateRow(index, 'email', e.target.value)} />
                    </td>
                    <td>
                      <Button variant="outline-danger" size="sm"
                        onClick={() => setRows(prev => prev.filter((_, i) => i !== index))}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <Button variant="outline-primary" size="sm" onClick={() => setRows(prev => [...prev, emptyRow()])}>
            Add rule
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save rules'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default EmailRoutingEditor;
